import { TRANSACTION_CLS } from "../../../utils/constants";
import { BasicProcessPhase } from "../../../utils/enums";
import { ApplyFunction, ApplyFunctionUtils } from "../ApplyFunction";
import { Transaction, TransactionMessage, TransactionMessageUtils } from "../cmds/Transaction";
import { CurrentPhaseUtils } from "../CurrentPhase";
import { Process, ProcessUtils } from "../Process";
import { DetailedSafeObject, DetailedSafeObjectUtils } from "./IDetailedSafeObjecet";

export interface DetailedTransaction extends DetailedSafeObject {
  readonly fee_rate: number;
  readonly asset: string;
  readonly amount: number;
  readonly address: string;
  readonly transactionMessage: TransactionMessage;
  readonly layer2data?: Record<string, any>;
  transferId?: string;
}

export const DetailedTransactionUtils = {
  create({
    fee_rate,
    asset,
    amount,
    address,
    transactionMessage,
    layer2data,
    isDeleted,
    isValid,
    applyFunction,
    originalObjectSIG,
    id,
    parentGroup,
    SIGNATURE,
    comment,
    process,
    nestedObjects,
    source,
    destination,
    slot,
    timestamp,
    last_modified,
    pending_objects,
    affected_by,
    execute = true,
    transferId,
  }: {
    fee_rate: number;
    asset: string;
    amount: number;
    address: string;
    transactionMessage: TransactionMessage;
    layer2data?: Record<string, any>;
    isDeleted: boolean;
    isValid: boolean;
    applyFunction: ApplyFunction;
    originalObjectSIG: string;
    id: string;
    parentGroup: string;
    SIGNATURE: string;
    comment: string;
    process: Process;
    nestedObjects: DetailedSafeObject[];
    source: string;
    destination: string;
    slot: number;
    timestamp: number;
    last_modified: number;
    pending_objects: DetailedSafeObject[];
    affected_by: DetailedSafeObject[];
    execute?: boolean;
    transferId?: string;
  }): DetailedTransaction {
    return {
      cls: TRANSACTION_CLS,
      fee_rate,
      asset,
      amount,
      address,
      transactionMessage,
      layer2data,
      isDeleted,
      isValid,
      applyFunction,
      originalObjectSIG,
      id,
      parentGroup,
      SIGNATURE,
      comment,
      process,
      nestedObjects,
      source,
      destination,
      slot,
      timestamp,
      last_modified,
      pending_objects,
      affected_by,
      execute,
      transferId,
    };
  },

  empty(): DetailedTransaction {
    const tms = Date.now();
    return DetailedTransactionUtils.create({
      fee_rate: 0,
      asset: "SOL",
      amount: 0,
      address: "",
      transactionMessage: TransactionMessageUtils.empty(),
      layer2data: undefined,
      isDeleted: false,
      isValid: false,
      applyFunction: ApplyFunctionUtils.empty(),
      originalObjectSIG: "",
      id: "",
      parentGroup: "",
      SIGNATURE: "",
      comment: "",
      process: ProcessUtils.create({
        currentPhase: CurrentPhaseUtils.create({
          index: BasicProcessPhase.NON_EXISTS,
          name: "",
          description: "",
          data: [],
          errors: [],
        }),
        phases: [],
      }),
      nestedObjects: [],
      source: "",
      destination: "",
      slot: -1,
      timestamp: tms,
      last_modified: tms,
      pending_objects: [],
      affected_by: [],
      transferId: "",
    });
  },

  toMap(detailedTransaction: DetailedTransaction): Record<string, any> {
    return {
      cls: detailedTransaction.cls,
      fee_rate: detailedTransaction.fee_rate.toString(),
      amount: detailedTransaction.amount.toString(),
      address: detailedTransaction.address,
      asset: detailedTransaction.asset,
      transactionMessage: TransactionMessageUtils.toMap(detailedTransaction.transactionMessage),
      layer2data: detailedTransaction.layer2data,
      isDeleted: detailedTransaction.isDeleted,
      isValid: detailedTransaction.isValid,
      applyFunction: ApplyFunctionUtils.toMap(detailedTransaction.applyFunction),
      originalObjectSIG: detailedTransaction.originalObjectSIG,
      id: detailedTransaction.id,
      parentGroup: detailedTransaction.parentGroup,
      SIGNATURE: detailedTransaction.SIGNATURE,
      comment: detailedTransaction.comment,
      process: ProcessUtils.toMap(detailedTransaction.process),
      nestedObjects: detailedTransaction.nestedObjects.map((no) =>
        DetailedSafeObjectUtils.toMap(no)
      ),
      source: detailedTransaction.source,
      destination: detailedTransaction.destination,
      slot: detailedTransaction.slot,
      timestamp: detailedTransaction.timestamp,
      last_modified: detailedTransaction.last_modified,
      pending_objects: detailedTransaction.pending_objects.map((po) =>
        DetailedSafeObjectUtils.toMap(po)
      ),
      affected_by: detailedTransaction.affected_by.map((ab) => DetailedSafeObjectUtils.toMap(ab)),
      execute: detailedTransaction.execute,
      object: {
        fee_rate: detailedTransaction.fee_rate.toString(),
        amount: detailedTransaction.amount.toString(),
        address: detailedTransaction.address,
        asset: detailedTransaction.asset,
      },
    };
  },

  toJson(detailedTransaction: DetailedTransaction): string {
    return JSON.stringify(DetailedTransactionUtils.toMap(detailedTransaction));
  },

  copyWith({
    fee_rate,
    asset,
    amount,
    address,
    transactionMessage,
    layer2data,
    isDeleted,
    isValid,
    applyFunction,
    originalObjectSIG,
    id,
    parentGroup,
    SIGNATURE,
    comment,
    process,
    nestedObjects,
    source,
    destination,
    slot,
    timestamp,
    last_modified,
    pending_objects,
    affected_by,
    execute,
    transferId,
  }: Partial<DetailedTransaction>): DetailedTransaction {
    return DetailedTransactionUtils.create({
      fee_rate: fee_rate ?? 0,
      asset: asset ?? "SOL",
      amount: amount ?? 0,
      address: address ?? "",
      transactionMessage: transactionMessage ?? TransactionMessageUtils.empty(),
      layer2data: layer2data ?? undefined,
      isDeleted: isDeleted ?? false,
      isValid: isValid ?? false,
      applyFunction: applyFunction ?? ApplyFunctionUtils.empty(),
      originalObjectSIG: originalObjectSIG ?? "",
      id: id ?? "",
      parentGroup: parentGroup ?? "",
      SIGNATURE: SIGNATURE ?? "",
      comment: comment ?? "",
      process:
        process ??
        ProcessUtils.create({
          currentPhase: CurrentPhaseUtils.create({
            index: BasicProcessPhase.NON_EXISTS,
            name: "",
            description: "",
            data: [],
            errors: [],
          }),
          phases: [],
        }),
      nestedObjects: nestedObjects ?? [],
      source: source ?? "",
      destination: destination ?? "",
      slot: slot ?? -1,
      timestamp: timestamp ?? Date.now(),
      last_modified: last_modified ?? Date.now(),
      pending_objects: pending_objects ?? [],
      affected_by: affected_by ?? [],
      execute: execute ?? true,
      transferId: transferId ?? "",
    });
  },

  fromTransaction({
    transaction,
    groupOwner,
    nestedObjects,
    applyFunction,
    SIGNATURE,
    isDeleted,
    isValid,
    originalObjectSIG,
    source,
    destination,
    affected_by,
  }: {
    transaction: Transaction;
    groupOwner?: string[];
    nestedObjects?: DetailedSafeObject[];
    applyFunction?: ApplyFunction;
    SIGNATURE?: string;
    isDeleted?: boolean;
    isValid?: boolean;
    originalObjectSIG?: string;
    source?: string;
    destination?: string;
    affected_by?: DetailedSafeObject[];
  }): DetailedTransaction {
    const tms = Date.now();
    return DetailedTransactionUtils.create({
      fee_rate: transaction.fee_rate,
      asset: transaction.asset,
      amount: transaction.amount,
      address: transaction.address,
      transactionMessage: transaction.transactionMessage || TransactionMessageUtils.empty(),
      layer2data: undefined,
      isDeleted: isDeleted ?? false,
      isValid: isValid ?? false,
      applyFunction: applyFunction || ApplyFunctionUtils.empty(),
      originalObjectSIG: originalObjectSIG ?? "",
      id: transaction.id,
      parentGroup: transaction.parentGroup,
      SIGNATURE: SIGNATURE ?? transaction.SIGNATURE,
      comment: transaction.comment,
      process: ProcessUtils.create({
        currentPhase: CurrentPhaseUtils.create({
          index: BasicProcessPhase.NON_EXISTS,
          name: "",
          description: "",
          data: [],
          errors: [],
        }),
        phases: [],
      }),
      nestedObjects: nestedObjects ?? [],
      source: source ?? "",
      destination: destination ?? "",
      slot: -1,
      timestamp: tms,
      last_modified: tms,
      pending_objects: [],
      affected_by: affected_by ?? [],
    });
  },
};
